import { __decorate, __metadata } from "tslib";
import { lazyAppContextConsumer } from '@1inch-community/core/lazy';
import { subscribe, translate } from '@1inch-community/core/lit-utils';
import '@1inch-community/ui-components/card';
import { SceneController, shiftAnimation } from '@1inch-community/ui-components/scene';
import { html, LitElement } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { when } from 'lit/directives/when.js';
import { combineLatest, tap } from 'rxjs';
import './account';
import './disconnect';
import './qrcode';
import './wallet';
import { WalletManagerRouteStyle } from './wallet-manager-route.style';
let WalletManagerRoute = class WalletManagerRoute extends LitElement {
    constructor() {
        super(...arguments);
        this.applicationContext = lazyAppContextConsumer(this);
        this.disconnectData = null;
        this.connectionLinkData = null;
        this.scene = new SceneController('account', {
            account: { lazyRender: true },
            wallets: { lazyRender: true },
            qrcode: {
                lazyRender: true,
            },
            disconnect: {},
        }, shiftAnimation());
        this.isWalletConnected = false;
    }
    static { this.tagName = 'inch-wallet-manager-route'; }
    static { this.styles = [WalletManagerRouteStyle, SceneController.styles()]; }
    firstUpdated() {
        const wallet = this.applicationContext.value.wallet;
        subscribe(this, combineLatest([wallet.data.isConnected$, wallet.data.activeAddress$]).pipe(tap(([isConnected, address]) => {
            if (this.isWalletConnected !== isConnected) {
                this.isWalletConnected = isConnected;
            }
            if (!isConnected && address === null && this.scene.activeScene !== 'wallets') {
                this.scene.resetScene();
                this.navigateTo('wallets', true);
            }
            if (isConnected && address && this.scene.activeScene !== 'account') {
                this.onBackPress();
            }
        })), { requestUpdate: false });
    }
    getAccountView() {
        return html `
      <inch-wallet-account-view
        .mobileView="${this.mobileView}"
        @changeWalletClick="${() => this.navigateTo('wallets')}"
        @disconnectEvent="${(event) => this.onDisconnectClick(event.detail.value)}"
      ></inch-wallet-account-view>
    `;
    }
    getWalletsView() {
        return html `
      <inch-wallet-manage
        @disconnectEvent="${(event) => this.onDisconnectClick(event.detail.value)}"
        @onUseConnectionLink="${(event) => this.onUseConnectionLink(event.detail.value)}"
      ></inch-wallet-manage>
    `;
    }
    getQrcodeView() {
        const data = { ...this.connectionLinkData };
        this.connectionLinkData = null;
        return html ` <inch-wallet-qrcode-view .data="${data}"></inch-wallet-qrcode-view>`;
    }
    getDisconnectView() {
        const data = { ...this.disconnectData };
        this.disconnectData = null;
        return html `
      <inch-wallet-disconnect-view
        .data="${data}"
        @onBackClick="${() => this.onBackPress()}"
      ></inch-wallet-disconnect-view>
    `;
    }
    getHeaderView() {
        switch (this.scene.activeScene) {
            case 'account':
                return this.accountHeaderView();
            case 'wallets':
                return this.walletsHeaderView();
            case 'qrcode':
                return this.qrcodeHeaderView();
            case 'disconnect':
                return this.disconnectHeaderView();
            default:
                throw new Error('unknown screen!', this.scene.activeScene);
        }
    }
    accountHeaderView() {
        return html ` <inch-card-header
      headerTextPosition="center"
      headerText="${translate('widgets.wallet-manager-route.account')}"
    >
      <inch-button
        slot="right-container"
        @click="${() => this.navigateTo('wallets')}"
        type="secondary"
        size="l"
      >
        <inch-icon icon="plus24"></inch-icon>
      </inch-button>
    </inch-card-header>`;
    }
    walletsHeaderView() {
        const title = this.isWalletConnected
            ? 'widgets.wallet-manager-route.wallets.manager'
            : 'widgets.wallet-manager-route.wallets.connect';
        return html ` <inch-card-header
      headerTextPosition="center"
      headerText="${translate(title)}"
      backButton="${ifDefined(this.isWalletConnected || undefined)}"
      @backCard="${() => this.onBackPress()}"
    >
    </inch-card-header>`;
    }
    qrcodeHeaderView() {
        return html ` <inch-card-header
      headerTextPosition="center"
      headerText="${translate('widgets.wallet-manager-route.wallets.qrcode.wallet-connect')}"
      backButton="${true}"
      @backCard="${() => this.onBackPress()}"
    >
    </inch-card-header>`;
    }
    disconnectHeaderView() {
        return html ` <inch-card-header
      headerTextPosition="center"
      headerText="${translate('widgets.wallet-manager-route.wallets.disconnect')}"
      backButton="${true}"
      @backCard="${() => this.onBackPress()}"
    >
    </inch-card-header>`;
    }
    onDisconnectClick(data) {
        this.disconnectData = data;
        this.navigateTo('disconnect');
    }
    onUseConnectionLink(data) {
        this.connectionLinkData = data;
        this.navigateTo('qrcode');
    }
    navigateTo(scene, immediate = false) {
        this.scene.nextTo(scene, immediate);
        this.requestUpdate();
    }
    onBackPress() {
        this.scene.back();
        this.requestUpdate();
    }
    render() {
        return html `
      <inch-card class="route-container" showShadow="${ifDefined(this.showShadow)}" overlayView>
        ${when(!this.mobileView, () => html ` <inch-card-close-overlay></inch-card-close-overlay> `)}
        ${this.getHeaderView()}
        ${this.scene.render({
            account: () => this.getAccountView(),
            wallets: () => this.getWalletsView(),
            qrcode: () => this.getQrcodeView(),
            disconnect: () => this.getDisconnectView(),
        })}
      </inch-card>
    `;
    }
};
__decorate([
    property({ type: Boolean, attribute: true }),
    __metadata("design:type", Boolean)
], WalletManagerRoute.prototype, "showShadow", void 0);
__decorate([
    property({ type: Boolean, attribute: true }),
    __metadata("design:type", Boolean)
], WalletManagerRoute.prototype, "mobileView", void 0);
__decorate([
    state(),
    __metadata("design:type", Boolean)
], WalletManagerRoute.prototype, "isWalletConnected", void 0);
WalletManagerRoute = __decorate([
    customElement(WalletManagerRoute.tagName)
], WalletManagerRoute);
export { WalletManagerRoute };
//# sourceMappingURL=wallet-manager-route.element.js.map